import { meaningIcon } from './meaning-icon'
import type { Lead } from './types'
import { api, mutate, readCache } from './api'
import { askAgainLater } from './followup'
import { replyForJob, replyGroup, replyQuiet, stageOf } from './rebook-message'
import { confirmNeed, customerConfirmNeed, detailsReasons, dueDate, effectiveCycle, noFixedCycle, takenOff } from './planning'
import { routeGen } from './route-state'
import { lineDrawing, type Drawing } from './picture-tiles'
import type { Customer } from './types'
import { clear, clockLabel, cycleLabel, el, shortDate, toast, todayISO } from './ui'

// The diary's compact action summary. The predicates here are the ONE
// definition of each section — the grid's view filters import the same
// functions, so a count in the summary and the filtered grid behind its link
// can never disagree. Everything in the top half is pure (no DOM) so the
// rules are testable headlessly; only renderAttention touches the document.

// The customer aggregate fields /api/customers rides along for this screen.
// They stay optional because the shared row type is owned elsewhere and a
// cached payload from before they existed simply won't have them — which is
// exactly what confirmFieldsKnown detects rather than misreading as zero.
export interface RoundCustomer extends Customer {
  // Earliest future visit the customer has AGREED. A later confirmed visit
  // counts even when an earlier unconfirmed suggestion is still on the books,
  // so risk checks must look past next_booked (the earliest booking of any
  // kind).
  next_confirmed?: string | null
  // Any future visit that is a real proposal (tentative rows are contact
  // prompts, not visits) and not yet agreed.
  has_unconfirmed?: 0 | 1 | null
  // A general follow-up the business promised the customer, as a date. A
  // plain customers column implemented alongside this work (type merged by
  // the manager), so it rides along in SELECT c.* — no aggregate here. It
  // stays optional: a payload from before the column existed simply has no
  // follow-ups scheduled, which is the same answer null gives.
  follow_up_on?: string | null
}

export type CycleRisk = 'slipping' | 'cold'

// Cycle risk — an ACTIVE customer whose cycle-computed due date has passed and
// who has agreed no future visit. Needs both a last clean and a cycle to say
// anything at all; more than two full cycles overdue is going cold, anything
// less is slipping. Only a CONFIRMED future visit clears the risk — an
// unconfirmed booking (including the app's own suggestion) is not a promise
// anyone has made yet. Unknown provenance (no next_confirmed field on the
// row) must not read as "definitely no confirmed visit", so it stays out.
export function cycleRisk(c: RoundCustomer, today: string = todayISO()): CycleRisk | null {
  if (c.status !== 'active') return null
  const cycle = effectiveCycle(c).days
  if (!c.last_clean || !cycle) return null
  if (c.next_confirmed === undefined) return null
  if (c.next_confirmed) return null
  const due = dueDate(c)
  if (!due || due >= today) return null
  const overdueDays = Math.round((Date.parse(today) - Date.parse(due)) / 86_400_000)
  return overdueDays > 2 * cycle ? 'cold' : 'slipping'
}

// Visits to confirm and Visits to check (Diary and Messages together, Liam 8 Oct 2026; docs/DIARY-AND-MESSAGES.md
// section 3): one rule, planning.ts confirmNeed, over every booked visit from today on (GET /api/customers
// upcoming_visits), the same rule the Diary's date strip counts by. A customer is to confirm when a visit still needs
// its day or its time agreed, and to check when a visit is still in the diary although they are paused, stopped,
// taken off, on Recanvass, lapsed, shelved as Needs a visit, or to be asked again after it. Roundly's suggestions and
// contact reminders are not visits to agree. Ad-hoc customers are not excluded: their one-off visits need agreeing too.
// Unknown (a saved copy from before upcoming_visits) counts as neither.
export function needsConfirming(c: RoundCustomer, today: string = todayISO()): boolean {
  const n = customerConfirmNeed(c, today)
  return !!n && n.day + n.time > 0
}
export function needsChecking(c: RoundCustomer, today: string = todayISO()): boolean {
  const n = customerConfirmNeed(c, today)
  return !!n && n.check > 0
}

// Nothing booked next, whatever the status — the same rule the grid's
// 'unbooked' view has always used.
export function noNextDate(c: Customer): boolean {
  return !c.next_booked
}

// Who Work's working lists (Due now, To confirm, No date yet, No next date, Slipping, Going cold, Follow up due, Parked)
// show: not someone on the Recanvass or Taken off list (customer-context.ts listQuiet), nor stopped. One rule for Work
// (grid.ts) and the counts the Diary's Needs you points at Work with, so "14 with no next date" opens 14 (bug hunt,
// 8 Oct 2026; agreed between the Diary and Work fixers).
export const inWorkLists = (c: Customer): boolean => !(c.customer_list || takenOff(c))

// An active customer with no date of any kind: never cleaned, nothing booked, no missed visit. After an
// import without dates they are most of a new round, and with no due date they are in no other list, so
// they would simply be forgotten (Liam, 29 Sep: "the rest could easily get lost, really easily").
// "They'll ask when they want one" (on request, ad-hoc, one off) needs no date, so it is not counted here.
export function noDateYet(c: Customer): boolean {
  return c.status === 'active' && !c.last_clean && !c.next_booked && !(c.overdue_bookings && c.overdue_bookings > 0) && !noFixedCycle(c)
}

// A general follow-up the business owes the customer — a promised call
// back, nothing to do with their cleaning cycle and nothing to do with
// money (that is Owed's job). Due once its date has arrived. Kept
// deliberately separate from cycleRisk: a follow-up can be due for a
// customer whose cycle is perfectly current, and a slipping customer with
// no follow-up promised stays out of this list. Not gated on
// confirmFieldsKnown — it reads a plain column, not the risk aggregates.
export function followUpDue(c: RoundCustomer, today: string = todayISO()): boolean {
  if (c.status !== 'active') return false
  const on = c.follow_up_on
  return !!on && on <= today
}

// Nothing to ask them today (Diary and Messages together, Liam 8 Oct 2026): messaged and still inside the week before
// the second message, a reply recorded that Messages files away (dates offered, booked after replying, ask again
// later, paused, stopped), "Not now, ask again on" a date still to come, shelved as Needs a visit, or on the
// Recanvass / Taken off lists. Messages leaves exactly these people out of everything it asks you to send; Needs you
// still lists the ones at risk, in their own words, but does not count them as needing the owner. A second message
// that is due, or no reply after two, IS the owner's next step, so those count on both sides.
export function nothingToAsk(c: RoundCustomer, today: string = todayISO()): boolean {
  if (c.messages_shelf === 'visit' || !!c.customer_list) return true
  if (askAgainLater(c, today)) return true
  if (replyQuiet(c, today)) return true
  return stageOf(c, today) === 'waiting'
}

// False when the payload predates the next_confirmed / has_unconfirmed
// aggregates: the risk and confirmation answers are UNKNOWN then, not zero.
export function confirmFieldsKnown(list: readonly Customer[]): boolean {
  return list.every((c) => 'has_unconfirmed' in c && 'next_confirmed' in c)
}

export interface AttentionSummary {
  slipping: RoundCustomer[]
  cold: RoundCustomer[]
  followup: RoundCustomer[]
  confirm: RoundCustomer[]
  check: RoundCustomer[]
  unbooked: RoundCustomer[]
  nodate: RoundCustomer[]
  details: { customer: RoundCustomer; reasons: string[] }[]
  riskKnown: boolean
  // Ids of the people above with nothing to ask today (nothingToAsk): listed, not counted as needing the owner.
  quiet: Set<number>
}

export function summariseRound(list: RoundCustomer[], today: string = todayISO()): AttentionSummary {
  const riskKnown = confirmFieldsKnown(list)
  const slipping: RoundCustomer[] = []
  const cold: RoundCustomer[] = []
  const followup: RoundCustomer[] = []
  const confirm: RoundCustomer[] = []
  const check: RoundCustomer[] = []
  const unbooked: RoundCustomer[] = []
  const nodate: RoundCustomer[] = []
  const details: { customer: RoundCustomer; reasons: string[] }[] = []
  const quiet = new Set<number>()
  for (const c of list) {
    if (nothingToAsk(c, today)) quiet.add(c.id)
    const risk = riskKnown ? cycleRisk(c, today) : null
    if (risk === 'slipping') slipping.push(c)
    else if (risk === 'cold') cold.push(c)
    if (followUpDue(c, today)) followup.push(c)
    if (needsConfirming(c, today)) confirm.push(c)
    if (needsChecking(c, today)) check.push(c)
    if (noNextDate(c)) unbooked.push(c)
    if (noDateYet(c)) nodate.push(c)
    const reasons = detailsReasons(c)
    if (reasons.length) details.push({ customer: c, reasons })
  }
  // Worst first: most overdue due date, longest-waiting follow-up, soonest
  // unagreed visit; the rest by name so the list is stable between renders.
  const dueOf = (c: RoundCustomer): string => dueDate(c) ?? ''
  slipping.sort((a, b) => dueOf(a).localeCompare(dueOf(b)) || a.id - b.id)
  cold.sort((a, b) => dueOf(a).localeCompare(dueOf(b)) || a.id - b.id)
  followup.sort((a, b) => (a.follow_up_on ?? '').localeCompare(b.follow_up_on ?? '') || a.id - b.id)
  const firstConfirm = (c: RoundCustomer): string => customerConfirmNeed(c, today)?.firstConfirm ?? ''
  const firstCheck = (c: RoundCustomer): string => customerConfirmNeed(c, today)?.firstCheck ?? ''
  confirm.sort((a, b) => firstConfirm(a).localeCompare(firstConfirm(b)) || a.id - b.id)
  check.sort((a, b) => firstCheck(a).localeCompare(firstCheck(b)) || a.id - b.id)
  unbooked.sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id)
  nodate.sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id)
  details.sort((a, b) => a.customer.name.localeCompare(b.customer.name) || a.customer.id - b.customer.id)
  return { slipping, cold, followup, confirm, check, unbooked, nodate, details, riskKnown, quiet }
}

// The Rebook tab's number: the two risk lists the diary summary already
// counts, added together. The tab badge and the Rebook page both read it from
// here, so a count in the nav and a count on the diary line cannot drift
// apart. Nothing about the definitions changes — this only adds them up.
export function rebookCount(s: AttentionSummary): number {
  return s.slipping.length + s.cold.length
}

// ---- rendering ----------------------------------------------------------------

let roundList: RoundCustomer[] | null = null
let noteFlags: NoteFlag[] | null = null
// Worker notes still to look at (0071), for the computer's Needs you (Liam, 14 Oct 2026, approved design).
let workerNotesNew = 0
let noteTotal = 0 // unseen in all; the list itself stops at 20
// Which lines of the box are open. "From the notes" starts open: it is news.
// Kept across redraws of the diary, like the old box's open state.
const openRows = new Set<string>(['notes'])
const SHOW_NAMES = 3
// "Not sure: ask them" is kept on the customer as the call list (to_call, the Work sheet's "Call?"), so it
// survives closing the app and shows on every phone (Liam, 29 Sep). Messaging them answers it.
export const notSure = (c: Pick<Customer, 'to_call' | 'rebook_messages'>): boolean => c.to_call === 1 && !((c.rebook_messages ?? 0) > 0)
// Set by the import finish page (and the welcome's "Give them dates") when customers came in with no dates:
// the next time the diary draws this box, "No date yet" opens and glows once so it cannot be missed.
const glowKey = 'roundly:nodate-glow'
export function markNoDateForGlow(): void {
  try {
    sessionStorage.setItem(glowKey, '1')
  } catch {
    // Private window: the line still shows, only without the glow.
  }
}
function takeGlow(): boolean {
  try {
    const on = sessionStorage.getItem(glowKey) === '1'
    if (on) sessionStorage.removeItem(glowKey)
    return on
  } catch {
    return false
  }
}
// The box drawn most recently, so "Give them dates" on the diary itself (where the hash does not change)
// can open and glow the line without a reload.
let showNoDateNow: (() => void) | null = null
export function goToNoDate(): void {
  markNoDateForGlow()
  if (window.location.hash.startsWith('#/schedule') && showNoDateNow) showNoDateNow()
  else window.location.hash = '#/schedule'
}

// The overdue wording for a risk row. Exported so the Rebook screen says
// exactly what the diary summary says about the same customer — one phrasing,
// one place to change it.
export function riskNote(c: RoundCustomer): string {
  const due = dueDate(c)
  const cyc = effectiveCycle(c)
  const label = cyc.guessed ? `about every ${Math.round((cyc.days ?? 0) / 7)} weeks, guessed from past visits` : (cycleLabel(c.cycle_days, c.cycle_text) || 'cycle')
  const reply = replyGroup(replyForJob(c), todayISO())
  return `was due ${due ? shortDate(due) : '—'} · ${label}${reply === 'datesoffered' ? ' · Dates offered, waiting' : reply === 'followup' && c.follow_up_on ? ` · Ask again ${shortDate(c.follow_up_on)}` : ''}`
}

function followUpNote(c: RoundCustomer): string {
  return `promised follow-up was due ${c.follow_up_on ? shortDate(c.follow_up_on) : '—'}`
}

// The summary block for the diary. It loads its own customer list (the
// schedule payload // A warning raised from a saved note (functions/lib/note-flags.ts).
export interface NoteFlag {
  id: number
  customer_id: number
  clean_id: number | null
  field: string
  kinds: string
  excerpt: string
  created_at: string
  customer_name: string
}

const KIND_LABELS: Record<string, string> = {
  complaint: 'Complaint',
  damage: 'Damage',
  stopping: 'Stopping or cancelling',
  money: 'Money',
}

const FIELD_LABELS: Record<string, string> = {
  note: 'visit note',
  voice: 'voice note',
  blocked_note: "couldn't do",
  notes: 'customer notes',
  call_notes: 'call notes',
  contact_note: 'contact note',
  job_notes: 'job notes',
}

export function flagKindLabel(kinds: string): string {
  return kinds.split(',').map((k) => KIND_LABELS[k] ?? k).join(' · ')
}

// The diary's summary box, "Needs you" (Liam, 2026-09-24: "not too
// overwhelming"). One line per thing that needs him, only when it has
// something in it; tap a line to see the first three names. The two big
// standing lists (no next date, missing a price or cycle) are one quiet line
// at the foot, linking to Work. It loads its own customer list (the schedule
// payload has no per-customer aggregates) through the same read cache the
// grid uses, and a failure here must never take the diary down with it.
// Lines the Diary adds under Needs you (Design B, Liam 8 Oct 2026): past visits to review, dates offered and waiting,
// contact reminders, which used to sit in Plan work. Each fills its own open line; a count of 0 draws nothing.
export interface NeedsExtra { key: string; label: string; count: number; tone?: 'amber' | 'slate'; fill: (into: HTMLElement) => void }
let extras: () => NeedsExtra[] = () => []
let refill: (() => void) | null = null
export function setNeedsExtras(fn: () => NeedsExtra[]): void { extras = fn; refill?.() }
// "Waiting for a reply" (Liam, 9 Oct 2026): everyone messaged who has not answered, by Messages' own rule, given by the
// Diary (schedule.ts, from rebook.ts awaitingReplies) so this file does not load Messages. ids: the people on it, who
// leave Slipping, Going cold and No date yet while they are there, so each person shows in one place.
export interface WaitingLine { ids: Set<number>; line: NeedsExtra | null }
let waitingLine: ((list: RoundCustomer[]) => WaitingLine) | null = null
export function setWaitingLine(fn: ((list: RoundCustomer[]) => WaitingLine) | null): void { waitingLine = fn; refill?.() }
// The phone diary's Needs you (round 2 of docs/plans/diary-due/PLAN.md): only the lines Liam acts on here are drawn, and
// the Diary hears every line each time the box fills, so its "Needs you · N" button and "Waiting on N" chip count the
// same people (the computer too, since round 3). null: every line.
export interface NeedsLine { key: string; label: string; tone: 'red' | 'amber' | 'slate'; count: number | null; fill: (into: HTMLElement) => void }
let onlyKeys: readonly string[] | null = null
let watch: ((lines: NeedsLine[], ready: boolean) => void) | null = null
export function setNeedsOnly(keys: readonly string[] | null): void { onlyKeys = keys }
export function setNeedsWatch(fn: ((lines: NeedsLine[], ready: boolean) => void) | null): void { watch = fn }
// "Give them dates" (the import's finish page, the welcome) on the phone diary: its First visit tab, not a line here.
let noDateOpener: (() => void) | null = null
export function setNoDateOpener(fn: (() => void) | null): void { noDateOpener = fn }
export function takeNoDateGlow(): boolean { return takeGlow() }

// Each Needs you line's picture (the restyle, 26 Oct 2026); a line not named here gets the three dots.
const NEEDS_DRAWINGS: Record<string, Drawing> = {
  replied: 'reply', waiting: 'bubble', workernotes: 'note', notes: 'note', followup: 'alarm', review: 'calendarTick',
}

export function renderAttention(root: HTMLElement): void {
  const box = el('section', { class: 'needs-box', 'aria-label': 'Needs you' })
  const head = el('div', { class: 'needs-head' }, el('h3', {}, 'Needs you'), el('small', {}, 'whole round'))
  const body = el('div', { class: 'needs-body' })
  box.append(head, body)
  root.append(box)

  let waiting: Lead[] = []
  let replied: Lead[] = []
  let showWaiting: (into: HTMLElement) => void = () => {}
  let showReplied: (into: HTMLElement) => void = () => {}
  const refreshWaiting = (): void => {
    waiting = []
    replied = []
    void import('./waiting-ok').then(async ({ loadEmailLeads, waitingEntries, repliedEntries }) => {
      const rows = await loadEmailLeads()
      if (gen !== routeGen || !body.isConnected) return
      waiting = rows.waiting
      replied = rows.replied
      showWaiting = into => waitingEntries(into, waiting, () => { refreshWaiting(); fill() })
      showReplied = into => repliedEntries(into, replied, () => { refreshWaiting(); fill() })
      fill()
    }).catch(() => { /* This line must never break the diary. */ })
  }

  type Line = NeedsLine // count null: unknown, not zero

  const toggleRow = (key: string, row: HTMLElement, button: HTMLElement): void => {
    const open = !row.classList.contains('needs-open')
    row.classList.toggle('needs-open', open)
    button.setAttribute('aria-expanded', open ? 'true' : 'false')
    if (open) openRows.add(key)
    else openRows.delete(key)
  }

  const lineRow = (line: Line): HTMLElement => {
    const open = openRows.has(line.key)
    const row = el('div', { class: `needs-row${open ? ' needs-open' : ''}` })
    const panelId = `needs-${line.key}`
    // The restyle Liam picked (26 Oct 2026, option 1 of https://claude.ai/artifact/4ZDFcwCiFngvmjCMFqC7iZ): each line a card
    // with its picture in a soft circle and the count as an amber pill (attention.css).
    const button = el('button', { type: 'button', class: 'needs-line', 'aria-expanded': open ? 'true' : 'false', 'aria-controls': panelId },
      ...(['awaiting', 'offers'].includes(line.key) ? [] : [el('span', { class: `needs-dot needs-${line.tone}`, 'aria-hidden': 'true' })]),
      ['awaiting', 'offers'].includes(line.key) ? meaningIcon('waiting', 'row')
        : el('span', { class: `needs-ring needs-ring-${line.tone}`, 'aria-hidden': 'true' }, lineDrawing(NEEDS_DRAWINGS[line.key] ?? 'dots', 18)),
      el('span', { class: 'needs-label' }, line.label),
      el('span', { class: 'needs-count' }, line.count === null ? '?' : String(line.count)),
      el('span', { class: 'needs-chev', 'aria-hidden': 'true' }))
    const panel = el('div', { class: 'needs-panel', id: panelId })
    line.fill(panel)
    button.addEventListener('click', () => toggleRow(line.key, row, button))
    row.append(button, panel)
    return row
  }

  // Diary and Messages as one (Liam, 8 Oct 2026): asking happens in Messages, so each row about asking opens that
  // person's card there; the name still opens their customer page.
  const messageButton = (c: RoundCustomer): HTMLElement => {
    // The key button on the row, so the lemon main button (Liam, 9 Oct 2026: it "blends into the other pills but its
    // probably the key button here"); the date pills stay plain.
    const b = el('button', { type: 'button', class: 'btn-primary needs-message', 'aria-label': `Message ${c.name}` }, 'Message')
    b.addEventListener('click', () => void import('./screens/rebook').then(({ openMessagesFor }) => openMessagesFor({ id: c.id, name: c.name }, 'customer')))
    return b
  }
  // workView false: a list with no Work view behind it, so every name is shown.
  const people = (view: string, rows: { customer: RoundCustomer; note: string }[], opts: { message?: boolean | ((c: RoundCustomer) => boolean); workView?: boolean } = {}) => (into: HTMLElement): void => {
    for (const r of opts.workView === false ? rows : rows.slice(0, SHOW_NAMES)) {
      const person = el('div', { class: 'needs-person' },
        el('a', { href: `#/customer/${r.customer.id}`, class: 'name-link' }, r.customer.name),
        el('span', {}, r.note))
      if (typeof opts.message === 'function' ? opts.message(r.customer) : opts.message) person.append(messageButton(r.customer))
      into.append(person)
    }
    // The number Work's list will show (inWorkLists), so the link never promises more than it opens.
    const inWork = rows.filter((r) => inWorkLists(r.customer)).length
    if (opts.workView !== false && rows.length > SHOW_NAMES && inWork) {
      into.append(el('a', { class: 'needs-all', href: `#/grid?view=${view}&status=all` }, `See all ${inWork} in Work →`))
    }
  }

  const seen = (flag: NoteFlag): void => {
    noteFlags = (noteFlags ?? []).filter((f) => f.id !== flag.id)
    noteTotal = Math.max(0, noteTotal - 1)
    fill()
    void mutate({ label: `Seen: note about ${flag.customer_name}`, method: 'PATCH', path: `/api/note-flags/${flag.id}`, body: {} }).then((ack) => {
      // Saved: fetch again, so a warning beyond the first 20 moves up.
      if (ack.ok) loadFlags()
      else if (ack.status >= 400 && ack.status < 500 && ack.status !== 404) toast("Couldn't mark that note as seen. It will show again next time.")
    })
  }

  // The same list as Today's bar opens: Seen, Next visit or Every visit for each.
  const workerNotesLine = (into: HTMLElement): void => {
    const open = el('button', { type: 'button', class: 'btn-secondary needs-worker-notes' }, `Look at ${workerNotesNew === 1 ? 'the note' : `the ${workerNotesNew} notes`}`)
    // Loaded when tapped, as Messages is from here: Needs you stays as light as before.
    open.addEventListener('click', () => void import('./worker-notes').then(({ openWorkerNotes }) => openWorkerNotes(() => loadWorkerNotes())))
    into.append(open)
  }

  const notesList = (into: HTMLElement): void => {
    for (const f of noteFlags ?? []) {
      const btn = el('button', { type: 'button', class: 'needs-seen' }, 'Seen')
      btn.addEventListener('click', () => seen(f))
      into.append(el('div', { class: 'needs-flag' },
        el('span', { class: 'needs-kind' }, flagKindLabel(f.kinds)),
        el('q', {}, f.excerpt),
        el('div', { class: 'needs-flag-meta' },
          el('span', {},
            el('a', { href: `#/customer/${f.customer_id}`, class: 'name-link' }, f.customer_name),
            ` · ${FIELD_LABELS[f.field] ?? 'note'} · ${shortDate(f.created_at.slice(0, 10))}`),
          btn)))
    }
  }

  // Where the customer list stands: the rest of the box waits on it, the
  // notes line does not (Codex review 2026-09-24: warnings vanished when the
  // customer list failed to load).
  let customers: 'loading' | 'failed' | 'ok' | 'cached' = roundList ? 'ok' : 'loading'
  let cachedAt: number | undefined

  const fill = (): void => {
    const s = summariseRound(roundList ?? [])
    const awaiting = customers === 'ok' || customers === 'cached' ? waitingLine?.(roundList ?? []) ?? null : null
    clear(body)
    if (customers === 'cached' && cachedAt) body.append(el('p', { class: 'needs-stale' }, `As saved on this phone at ${clockLabel(cachedAt)}, may be out of date.`))
    const lines: Line[] = []
    // A customer replied to the quote they were emailed (Liam, 9 Oct 2026: replies "land straight in roundly app").
    if (replied.length) lines.push({ key: 'replied', label: 'Replied to your quote', tone: 'amber', count: replied.length, fill: showReplied })
    if (waiting.length) lines.push({ key: 'waiting', label: 'Waiting for your OK', tone: 'amber', count: waiting.length, fill: showWaiting })
    if (workerNotesNew) lines.push({ key: 'workernotes', label: 'Worker notes', tone: 'amber', count: workerNotesNew, fill: workerNotesLine })
    if (noteFlags && noteFlags.length) lines.push({ key: 'notes', label: 'From the notes', tone: 'red', count: Math.max(noteTotal, noteFlags.length), fill: notesList })
    if (customers === 'loading' || customers === 'failed') {
      if (body.isConnected) watch?.(lines, false)
      const drawn = onlyKeys ? lines.filter((l) => onlyKeys!.includes(l.key)) : lines
      if (drawn.length) {
        const list = el('div', { class: 'needs-lines' })
        for (const line of drawn) list.append(lineRow(line))
        body.append(list)
      }
      body.append(el('p', { class: 'needs-none' }, customers === 'loading' ? 'Loading…' : "Couldn't load the round right now. The diary below is unaffected."))
      return
    }
    // The people waiting on an answer, and those offered days, together (Liam approved the drawing, 9 Oct 2026). Slipping,
    // Going cold and No date yet are the Diary's Due and its First visit tab (round 3), and its visits to confirm or check
    // are said on their own days, so none of those is a line here (tidy-up item 4, 20 Oct 2026: the lines only Original
    // drew went with it).
    const more = extras()
    const offers = more.find((x) => x.key === 'offers')
    if (awaiting?.line?.count) lines.push({ ...awaiting.line, tone: awaiting.line.tone ?? 'slate' })
    if (offers?.count) lines.push({ key: offers.key, label: offers.label, tone: offers.tone ?? 'slate', count: offers.count, fill: offers.fill })
    // People with nothing to ask today (summary.quiet: asked and waiting, dates offered, ask again later, Needs a visit,
    // Recanvass, Taken off) stay listed, last, in their own words, but are not counted: Messages has nothing to send them.
    const asksFirst = (list: RoundCustomer[]): RoundCustomer[] => [...list.filter((c) => !s.quiet.has(c.id)), ...list.filter((c) => s.quiet.has(c.id))]
    // Follow-up is its own question (not cycle lateness, not money), so it
    // is counted whatever the risk aggregates say.
    if (s.followup.length) lines.push({ key: 'followup', label: 'Follow-ups due', tone: 'amber', count: s.followup.filter((c) => !s.quiet.has(c.id)).length, fill: people('followup', asksFirst(s.followup).map((c) => ({ customer: c, note: followUpNote(c) })), { message: true }) })
    for (const x of more) if (x.count && x !== offers) lines.push({ key: x.key, label: x.label, tone: x.tone ?? 'slate', count: x.count, fill: x.fill })
    if (body.isConnected) watch?.(lines, true) // a box the diary has since replaced says nothing
    const drawn = onlyKeys ? lines.filter((l) => onlyKeys!.includes(l.key)) : lines
    if (drawn.length) {
      const list = el('div', { class: 'needs-lines' })
      for (const line of drawn) list.append(lineRow(line))
      body.append(list)
    } else {
      body.append(el('p', { class: 'needs-none' }, 'Nothing needs you right now.'))
    }

    // The two standing lists: worth a look now and then, never a daily job.
    const quiet: HTMLElement[] = []
    const unbooked = s.unbooked.filter(inWorkLists).length
    if (unbooked) quiet.push(el('a', { href: '#/grid?view=unbooked&status=all' }, `${unbooked} with no next date`))
    if (s.details.length) quiet.push(el('a', { href: '#/grid?view=details&status=all' }, `${s.details.length} missing a price or cycle`))
    if (quiet.length) {
      const foot = el('p', { class: 'needs-foot' }, 'Also in Work: ')
      quiet.forEach((a, i) => foot.append(...(i ? [' · ', a] : [a])))
      body.append(foot)
    }
  }

  fill()
  refill = (): void => { if (body.isConnected) fill() }
  // "Give them dates" on the Diary itself: its First visit tab (the Due list), not a line here.
  showNoDateNow = (): void => { takeGlow(); noDateOpener?.() }
  const gen = routeGen
  refreshWaiting()
  // The warnings from the notes load on their own: if they fail, the rest of
  // the box still shows, just without that line.
  function loadFlags(): void {
    void api<{ flags: NoteFlag[]; total?: number }>('/api/note-flags')
      .then((data) => {
        if (gen !== routeGen || !body.isConnected) return
        noteFlags = data.flags
        noteTotal = typeof data.total === 'number' ? data.total : data.flags.length
        fill()
      })
      .catch(() => {
        /* no line from the notes this time; everything else is unaffected */
      })
  }
  loadFlags()
  function loadWorkerNotes(): void {
    void api<{ new_count: number }>('/api/worker-notes')
      .then((data) => {
        if (gen !== routeGen || !body.isConnected) return
        workerNotesNew = data.new_count
        fill()
      })
      .catch(() => {
        /* no Worker notes line this time (not an owner, or no signal); everything else is unaffected */
      })
  }
  loadWorkerNotes()
  void (async () => {
    try {
      const data = await api<{ customers: RoundCustomer[] }>('/api/customers', 'customers')
      if (gen !== routeGen || !body.isConnected) return
      roundList = data.customers
      customers = 'ok'
      fill()
    } catch {
      if (gen !== routeGen || !body.isConnected) return
      // Offline / error: the last good list from this phone, behind its own
      // timestamp, rather than counts that pretend to be zero.
      const cached = readCache<{ customers: RoundCustomer[] }>('customers')
      if (cached) {
        roundList = cached.data.customers
        customers = 'cached'
        cachedAt = cached.at
      } else if (!roundList) {
        customers = 'failed'
      }
      fill()
    }
  })()
}
